// Small show/episode building blocks: status badge, waveform, copy field,
// audio upload.

import { useRef, useState, type DragEvent } from 'react';
import type { EpisodeStatus } from '@podcastwiz-app/shared';
import { Badge, type Tone } from '../ui/Feedback';
import { Button } from '../ui/Button';

const STATUS: Record<EpisodeStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  editing: { label: 'Editing', tone: 'accent' },
  ready: { label: 'Ready', tone: 'accent' },
  scheduled: { label: 'Scheduled', tone: 'warning' },
  published: { label: 'Published', tone: 'success' },
  archived: { label: 'Archived', tone: 'neutral' },
};

export function StatusBadge({ status }: { status: EpisodeStatus }) {
  const s = STATUS[status] ?? { label: status, tone: 'neutral' as const };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

/** Peak waveform as an SVG of mirrored bars; `peaks` are 0..1. */
export function Waveform({ peaks, bars = 120, className = '' }: { peaks: number[]; bars?: number; className?: string }) {
  if (peaks.length === 0) return null;
  const step = Math.max(1, Math.floor(peaks.length / bars));
  const sampled: number[] = [];
  for (let i = 0; i < peaks.length; i += step) {
    sampled.push(Math.max(...peaks.slice(i, i + step)));
  }
  const w = 100 / sampled.length;
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label="Audio waveform" className={`h-16 w-full ${className}`}>
      {sampled.map((p, i) => {
        const h = Math.max(0.5, p * 38);
        return <rect key={i} x={i * w + w * 0.15} y={20 - h / 2} width={w * 0.7} height={h} rx={0.3} className="fill-accent" />;
      })}
    </svg>
  );
}

/** A small Copy button; the label flips to "Copied" for two seconds. */
export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

export function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <span className="block text-sm font-medium text-text">{label}</span>
      <div className="flex gap-2">
        <input
          readOnly
          aria-label={label}
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-text"
        />
        <Button variant="secondary" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

const AUDIO_ACCEPT = 'audio/*,video/mp4,.wav,.mp3,.m4a,.aac,.flac,.ogg';

interface AudioDropProps {
  onFile: (file: File) => void;
  /** 0..1 while uploading, null when idle. */
  progress: number | null;
  label: string;
}

/** File picker + drop zone with an upload progress bar. */
export function AudioDrop({ onFile, progress, label }: AudioDropProps) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const uploading = progress !== null;

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const file = e.dataTransfer.files[0];
    if (file && !uploading) onFile(file);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
        over ? 'border-accent bg-accent-soft' : 'border-border bg-surface'
      }`}
    >
      {uploading ? (
        <div className="space-y-2">
          <p className="text-sm text-text">Uploading… {Math.round(progress * 100)}%</p>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="h-2 overflow-hidden rounded-full bg-surface-2"
          >
            <div className="h-full bg-accent transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted">Drop an audio file here, or</p>
          <Button variant="secondary" className="mt-2" onClick={() => input.current?.click()}>
            {label}
          </Button>
          <input
            ref={input}
            type="file"
            accept={AUDIO_ACCEPT}
            hidden
            data-testid="audio-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />
          <p className="mt-2 text-xs text-muted">WAV, MP3, M4A, FLAC or OGG, up to 500 MB.</p>
        </>
      )}
    </div>
  );
}
